// External dependencies
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import {
  getSettings,
  updateSettings,
} from '@/modules/cms/client/api/admin-cms.api';

/**
 * Themes offered in the picker.
 *
 * Must stay in step with the `THEMES` enum on the server model — the server
 * rejects anything outside it, so a drift here surfaces as a save failure
 * rather than a silently applied theme.
 */
const THEMES = [
  { value: 'default', label: 'Default', swatch: '#ffffff', ink: '#12b591' },
  { value: 'dark', label: 'Dark', swatch: '#1c1f24', ink: '#12b591' },
];

export default function AdminCmsSettings() {
  const [settings, setSettings] = useState({
    siteName: '',
    tagline: '',
    theme: 'default',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    getSettings()
      .then(data =>
        setSettings({
          siteName: data.siteName || '',
          tagline: data.tagline || '',
          theme: data.theme || 'default',
        }),
      )
      .catch(() => setError('Failed to load settings.'))
      .finally(() => setIsLoading(false));
  }, []);

  const setField = (name, value) => {
    setSettings(previous => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const saved = await updateSettings(settings);
      setSettings({
        siteName: saved.siteName || '',
        tagline: saved.tagline || '',
        theme: saved.theme || 'default',
      });
      setSuccess('Settings saved. The theme applies on the next page load.');
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const activeTheme =
    THEMES.find(theme => theme.value === settings.theme) || THEMES[0];

  if (isLoading) {
    return (
      <AdminLayout>
        <CmsAdminNav />
        <div className="container">
          <p>Loading settings…</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h2>Settings</h2>
            <p>Site identity and appearance.</p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="cms-settings-site-name">Site name</label>
                <input
                  id="cms-settings-site-name"
                  type="text"
                  className="form-control"
                  value={settings.siteName}
                  onChange={event => setField('siteName', event.target.value)}
                  maxLength={100}
                  placeholder="Left blank, falls back to the configured name"
                />
                <span className="help-block">
                  Used for the browser tab title and social sharing previews.
                </span>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="cms-settings-tagline">Tagline</label>
                <input
                  id="cms-settings-tagline"
                  type="text"
                  className="form-control"
                  value={settings.tagline}
                  onChange={event => setField('tagline', event.target.value)}
                  maxLength={250}
                  placeholder="Left blank, falls back to the configured description"
                />
                <span className="help-block">
                  Used as the site description for search engines and link
                  previews.
                </span>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="cms-settings-theme">Theme</label>
                <select
                  id="cms-settings-theme"
                  className="form-control"
                  value={settings.theme}
                  onChange={event => setField('theme', event.target.value)}
                >
                  {THEMES.map(theme => (
                    <option key={theme.value} value={theme.value}>
                      {theme.label}
                    </option>
                  ))}
                </select>
                <span className="help-block">
                  Applies to every visitor on their next page load.
                </span>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="cms-settings-swatch">Preview</label>
                <div
                  id="cms-settings-swatch"
                  style={{
                    background: activeTheme.swatch,
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '18px',
                    color: activeTheme.ink,
                  }}
                >
                  <strong>{settings.siteName || 'Site name'}</strong>
                  <div style={{ opacity: 0.75 }}>
                    {settings.tagline || 'Tagline'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}

AdminCmsSettings.propTypes = {};
